import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "../../components/ui";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { ownerSelector } from "../../store/store";
import { createCampaign } from "../../store/features/ownerActions";
import { fetchOwner } from "../../utils";
import {
  clearPersistedDraftCampaignId,
  type CampaignFlowState,
  fetchCampaignDraft,
  resolveFlowScope,
  resolveCampaignIdFromStateOrSearch,
  normalizeCampaignListIds,
  hasLinkedCampaignLists,
} from "./campaignDraftFlow";
import { useDispatch } from "react-redux";
import type { Action, ThunkDispatch } from "@reduxjs/toolkit";
import type { RootState } from "../../store/store";

export default function CampaignCreateReviewLaunchStep() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const { run: runLaunch, isPending: isLaunching } = useAsyncAction();
  const { currentUser, owner, linkedOwners, currentWorkspace } = useSelector(ownerSelector);
  const flowState = (location.state as CampaignFlowState | null) || {};
  const campaignId = resolveCampaignIdFromStateOrSearch(flowState, searchParams.get("campaignId"));
  const { scope, stepOnePayload, stepTwoPayload, stepThreePayload, stepThreeRehydration, stepFourPayload } =
    flowState;
  const resolvedScope = React.useMemo(
    () =>
      resolveFlowScope({
        flowState,
        owner,
        currentUser,
        currentWorkspace,
      }),
    [flowState, owner, currentUser, currentWorkspace]
  );
  const stateCampaignName = String(stepOnePayload?.campaignName || flowState.campaignName || "").trim();
  const [draftCampaignName, setDraftCampaignName] = React.useState("");
  const [draftSenderOwnerId, setDraftSenderOwnerId] = React.useState("");
  const [resolvedSenderName, setResolvedSenderName] = React.useState("");

  const normalizeId = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      if (typeof value._id === "string") return value._id;
      if (typeof value.$oid === "string") return value.$oid;
    }
    try {
      return String(value);
    } catch {
      return "";
    }
  };

  const resolveWorkspaceId = (): string => {
    if (resolvedScope.workspaceId) return String(resolvedScope.workspaceId);
    if (typeof currentWorkspace === "string") {
      try {
        const parsed = JSON.parse(currentWorkspace || "null");
        return normalizeId(parsed?._id);
      } catch {
        return "";
      }
    }
    return normalizeId((currentWorkspace as any)?._id);
  };

  React.useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const workspaceId = resolveWorkspaceId();
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId:
            String(resolvedScope.ownerId || "").trim() ||
            String(stepTwoPayload?.senderOwnerId || "").trim() ||
            String(owner?._id || "").trim() ||
            undefined,
          userId: resolvedScope.userId || currentUser?.userId || "",
          workspaceId,
        });
        if (cancelled) return;
        const name = String(draftResponse?.campaign?.campaignName || "").trim();
        if (!stateCampaignName && name) setDraftCampaignName(name);
        const draftOwnerId = String(draftResponse?.campaign?.ownerId || "").trim();
        if (draftOwnerId) setDraftSenderOwnerId(draftOwnerId);
      } catch {
        // ignore; headline falls back to neutral copy
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    campaignId,
    stateCampaignName,
    resolvedScope.ownerId,
    resolvedScope.userId,
    resolvedScope.workspaceId,
    stepTwoPayload?.senderOwnerId,
    owner?._id,
    currentUser?.userId,
    currentWorkspace,
  ]);

  const campaignName = stateCampaignName || draftCampaignName;
  const senderOwnerId = String(
    stepTwoPayload?.senderOwnerId || draftSenderOwnerId || ""
  ).trim();
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!senderOwnerId) {
        setResolvedSenderName("");
        return;
      }
      const linkedOwnerName = Array.isArray(linkedOwners)
        ? String(
            linkedOwners.find((entry: any) => String(entry?.ownerId) === senderOwnerId)?.name || ""
          ).trim()
        : "";
      if (linkedOwnerName) {
        setResolvedSenderName(linkedOwnerName);
        return;
      }
      try {
        const ownerData = await fetchOwner(senderOwnerId);
        if (cancelled) return;
        const fetchedName = String(
          ownerData?.name ||
            ownerData?.owner?.name ||
            ownerData?.data?.name ||
            ownerData?.email ||
            ""
        ).trim();
        setResolvedSenderName(fetchedName);
      } catch {
        if (!cancelled) setResolvedSenderName("");
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [senderOwnerId, linkedOwners]);
  const senderName =
    resolvedSenderName ||
    String(owner?.name || "").trim() ||
    "your sender";

  const handleLaunchCampaign = () =>
    runLaunch(async () => {
      let resolvedCampaignName =
        String(stepOnePayload?.campaignName || "").trim() ||
        String(flowState.campaignName || "").trim();
      const stepThreeActions = stepThreePayload?.actions;
      let resolvedActions = Array.isArray(stepThreeActions) ? stepThreeActions : [];
      const selectedSenderOwnerId = String(stepTwoPayload?.senderOwnerId || "").trim();
      // Campaign executes as the sender chosen in step 2 (multi-sender); otherwise same as list owner.
      let campaignOwnerId =
        selectedSenderOwnerId ||
        String(resolvedScope.ownerId || "").trim() ||
        String(owner?._id || "").trim();
      let listIds = normalizeCampaignListIds(
        stepOnePayload?.leadsListIds,
        stepOnePayload?.leadsListId
      );
      let listId = listIds[0] || "";
      let workspaceId = resolveWorkspaceId();
      let prospectIds: string[] = [];
      let sequenceTemplateId: string | null | undefined;

      // Source of truth for launch context: persisted draft campaign.
      if (campaignId) {
        try {
          const draftResponse = await fetchCampaignDraft({
            campaignId,
            ownerId: campaignOwnerId || undefined,
            userId: resolvedScope.userId || currentUser?.userId || "",
            workspaceId,
          });
          const campaign = draftResponse?.campaign;
          const draftOwnerId = normalizeId(campaign?.ownerId);
          const draftListIds = normalizeCampaignListIds(campaign?.listIds, campaign?.listId);
          const draftListId = draftListIds[0] || normalizeId(campaign?.listId);
          const draftWorkspaceId = normalizeId(campaign?.workspaceId);
          const draftCampaignName = String(campaign?.campaignName || "").trim();

          // Prefer draft values to avoid route-state scope drift after reload/edit.
          if (draftOwnerId) campaignOwnerId = draftOwnerId;
          if (draftListIds.length) listIds = draftListIds;
          if (draftListId) listId = draftListId;
          if (draftWorkspaceId) workspaceId = draftWorkspaceId;
          if (draftCampaignName) resolvedCampaignName = draftCampaignName;

          if (Array.isArray(campaign?.actions) && campaign.actions.length > 0) {
            resolvedActions = campaign.actions;
          }

          prospectIds = Array.isArray(campaign?.prospects)
            ? campaign.prospects
                .map((prospectId: any) => normalizeId(prospectId))
                .filter(Boolean)
            : [];

          if (Object.prototype.hasOwnProperty.call(campaign || {}, "sequenceTemplateId")) {
            sequenceTemplateId = campaign?.sequenceTemplateId
              ? normalizeId(campaign.sequenceTemplateId)
              : null;
          }
        } catch (error) {
          // Keep existing guard/UX below if draft hydration fails.
        }
      }

      console.log("campaignOwnerId(final)", campaignOwnerId);
      console.log("listIds(final)", listIds);
      console.log("workspaceId(final)", workspaceId);

      if (!resolvedCampaignName) {
        toast.error("Campaign name is missing. Please go back to step 1 and save campaign name.");
        return;
      }

      if (!campaignOwnerId || !hasLinkedCampaignLists(listIds, listId) || !workspaceId) {
        toast.error("Missing owner, workspace, or list context for campaign payload.");
        return;
      }

      if (!Array.isArray(resolvedActions) || resolvedActions.length === 0) {
        toast.error("Campaign actions are missing. Please go back to step 3 and save sequence.");
        return;
      }

      if (prospectIds.length === 0) {
        toast.error("Selected lists have no prospects.");
        return;
      }

      const loader = toast.loading("Launching campaign...");

      const normalizedActions = resolvedActions.map((action: any) => {
        if (!action || typeof action !== "object") return action;
        if (action.delay === undefined || action.delay === null) return action;
        return {
          ...action,
          delay: String(action.delay),
        };
      });

      const finalCreatePayload = {
        campaignId,
        campaignName: resolvedCampaignName,
        listId,
        listIds,
        actions: normalizedActions,
        prospects: prospectIds,
        ownerId: campaignOwnerId,
        executorOwnerId: selectedSenderOwnerId || campaignOwnerId,
        senderOwnerIds: stepTwoPayload?.senderOwnerIds || [],
        userId: resolvedScope.userId || currentUser?.userId || "",
        workspaceId,
        excludeListId: stepOnePayload?.excludeListId || undefined,
        excludeRules: stepOnePayload?.excludeRules || undefined,
        scheduleMode: stepFourPayload?.scheduleMode || "OWNER_DEFAULT",
        schedule: stepFourPayload?.schedule || undefined,
        sequenceRehydration: stepThreeRehydration || undefined,
        ...(sequenceTemplateId !== undefined
          ? { sequenceTemplateId: sequenceTemplateId || null }
          : {}),
      };

      console.log("[Campaign Create] final payload preview", finalCreatePayload);
      const createResult = await dispatch(createCampaign(finalCreatePayload as any));
      if ((createResult as any)?.meta?.requestStatus !== "fulfilled") {
        const serverError =
          (createResult as any)?.payload ||
          (createResult as any)?.error?.message;
        toast.update(loader, {
          render:
            typeof serverError === "string" && serverError.trim()
              ? serverError
              : "Unable to launch campaign.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        });
        return;
      }
      toast.update(loader, {
        render: "Campaign launched successfully.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      clearPersistedDraftCampaignId();
      navigate("/campaign-management");
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        <div className="mx-auto flex w-full max-w-[980px] items-center justify-center rounded-[8px] bg-white px-4 py-8 sm:px-6 sm:py-12">
          <div className="flex flex-col items-center text-center">
            <img src="/icons/hooray_top.svg" alt="" aria-hidden="true" className="h-14 w-14" />

            <h2 className="mt-5 text-[1.5rem] font-semibold leading-none text-dark-black">Hooray! 🎉</h2>

            <p className="mt-3 text-sm text-dark-grey">
              You are about to launch{" "}
              <span className="font-semibold text-dark-black">
                {campaignName ? `${campaignName} ` : "this "}
              </span>
              campaign using{" "}
              <span className="font-semibold text-dark-black">
                {senderName || "your sender"}
              </span>
              .
            </p>
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-light-grey bg-white px-4 py-4 sm:px-6">
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            iconSrc="/icons/arrow_right.svg"
            showAfter
            isLoading={isLaunching}
            onClick={handleLaunchCampaign}
          >
            {isLaunching
              ? "Launching..."
              : campaignName
                ? `Launch ${campaignName}`
                : "Launch campaign"}
          </Button>
        </div>
      </div>
    </div>
  );
}
